/**
 * ScrollableFilmstrip - Horizontal scroll container with navigation arrows and fade indicators
 *
 * Provides visual feedback when content overflows:
 * - Left/Right scroll arrows appear when there's more content
 * - Fade gradients indicate scrollable content
 * - Smooth scroll on arrow click
 * - Wheel scroll support
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ScrollableFilmstripProps {
  children: React.ReactNode;
  className?: string;
  /** Height of the container */
  height?: string;
  /** Gap between items */
  gap?: string;
  /** Padding X */
  paddingX?: string;
  /** Show filmstrip (for animation) */
  isVisible?: boolean;
}

export const ScrollableFilmstrip: React.FC<ScrollableFilmstripProps> = ({
  children,
  className,
  height = 'h-[76px]',
  gap = 'gap-2',
  paddingX = 'px-4',
  isVisible = true,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll state
  const updateScrollState = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    // Add small threshold to account for rounding errors
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // Update scroll state on mount, resize, and children changes
  useEffect(() => {
    updateScrollState();

    const container = scrollRef.current;
    if (!container) return;

    // Listen for scroll events
    container.addEventListener('scroll', updateScrollState, { passive: true });

    // Create resize observer for container size changes
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    // Also observe window resize
    window.addEventListener('resize', updateScrollState);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, children]);

  // Re-check when visibility changes (e.g., panels open/close)
  useEffect(() => {
    if (isVisible) {
      // Small delay to let layout settle
      const timer = setTimeout(updateScrollState, 100);
      return () => clearTimeout(timer);
    }
  }, [isVisible, updateScrollState]);

  // Scroll handlers
  const scrollBy = useCallback((direction: 'left' | 'right') => {
    const container = scrollRef.current;
    if (!container) return;

    // Scroll by approximately 3 slides worth
    const scrollAmount = container.clientWidth * 0.7;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  // Handle mouse wheel for horizontal scrolling
  const handleWheel = useCallback((e: React.WheelEvent) => {
    const container = scrollRef.current;
    if (!container) return;

    // If there's horizontal scroll potential, convert vertical wheel to horizontal
    if (container.scrollWidth > container.clientWidth) {
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    }
  }, []);

  return (
    <div
      className={cn(
        "relative flex items-center",
        "transition-all duration-300 ease-out",
        isVisible ? `${height} opacity-100` : "h-0 opacity-0",
        className
      )}
    >
      {/* Left Fade Gradient + Arrow */}
      <div
        className={cn(
          "absolute left-0 top-0 bottom-0 z-10 flex items-center",
          "transition-opacity duration-200",
          canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        {/* Fade gradient */}
        <div className="absolute left-0 top-0 bottom-0 w-8 md:w-16 bg-gradient-to-r from-background via-background/80 to-transparent pointer-events-none" />

        {/* Arrow button - hidden on mobile (use swipe instead) */}
        <button
          onClick={() => scrollBy('left')}
          className={cn(
            "relative z-10 ml-1 w-6 h-6 md:w-8 md:h-8 rounded-full hidden md:flex",
            "bg-white/90 dark:bg-zinc-800/90 backdrop-blur-sm",
            "border border-gray-200 dark:border-zinc-700",
            "shadow-lg hover:shadow-xl",
            "items-center justify-center",
            "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white",
            "transition-all duration-200",
            "hover:scale-110 active:scale-95"
          )}
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        onWheel={handleWheel}
        className={cn(
          "flex-1 flex items-center overflow-x-auto",
          gap,
          paddingX,
          // Smooth scrolling
          "scroll-smooth",
          // Custom scrollbar styling class
          "filmstrip-scrollbar"
        )}
      >
        {children}
      </div>

      {/* Inline styles for scrollbar - works without tailwind-scrollbar plugin */}
      <style>{`
        .filmstrip-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
        }
        .filmstrip-scrollbar:hover {
          scrollbar-color: rgba(156, 163, 175, 0.8) transparent;
        }
        .filmstrip-scrollbar::-webkit-scrollbar {
          height: 6px;
        }
        .filmstrip-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .filmstrip-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.5);
          border-radius: 3px;
        }
        .filmstrip-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(156, 163, 175, 0.8);
        }
        .dark .filmstrip-scrollbar {
          scrollbar-color: rgba(113, 113, 122, 0.5) transparent;
        }
        .dark .filmstrip-scrollbar:hover {
          scrollbar-color: rgba(113, 113, 122, 0.8) transparent;
        }
        .dark .filmstrip-scrollbar::-webkit-scrollbar-thumb {
          background-color: rgba(113, 113, 122, 0.5);
        }
        .dark .filmstrip-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: rgba(113, 113, 122, 0.8);
        }
      `}</style>

      {/* Right Fade Gradient + Arrow */}
      <div
        className={cn(
          "absolute right-0 top-0 bottom-0 z-10 flex items-center",
          "transition-opacity duration-200",
          canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        {/* Fade gradient */}
        <div className="absolute right-0 top-0 bottom-0 w-8 md:w-16 bg-gradient-to-l from-background via-background/80 to-transparent pointer-events-none" />

        {/* Arrow button - hidden on mobile (use swipe instead) */}
        <button
          onClick={() => scrollBy('right')}
          className={cn(
            "relative z-10 mr-1 w-6 h-6 md:w-8 md:h-8 rounded-full hidden md:flex",
            "bg-white/90 dark:bg-zinc-800/90 backdrop-blur-sm",
            "border border-gray-200 dark:border-zinc-700",
            "shadow-lg hover:shadow-xl",
            "items-center justify-center",
            "text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white",
            "transition-all duration-200",
            "hover:scale-110 active:scale-95"
          )}
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ScrollableFilmstrip;
